import { ClientCredit } from './client-credit.js';
import { AgendaPackageChoice } from './agenda-package-choice.js';
import { useState } from 'react';
import { RecordsSidePanel, type RecordsPanel } from '../modules/records/index.js';
import {
  CompanyScheduling,
  SchedulingUnitsProvider,
  SchedulingPrepaidProvider,
  SchedulingBalancesLinkProvider,
  SchedulingCreditProvider,
} from '../modules/scheduling/index.js';
import { ChooseSchedulingUnit } from './choose-scheduling-unit.js';
import { useCompanyScope } from './company-context.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import type { SchedulingView } from './list-views.js';
import type { ViewSetter } from './view-state.js';

export function CompanySchedulingScreen({
  view,
  onView,
  onOpenClosing,
  onOpenClient,
  onOpenClientBalances,
  onOpenProfessionals,
}: {
  onOpenProfessionals?: (() => void) | undefined;
  onOpenClientBalances?: ((clientId: string) => void) | undefined;
  view: SchedulingView;
  onOpenClosing: (appointmentId: string) => void;
  onView: ViewSetter<SchedulingView>;
  onOpenClient: (clientId: string) => void;
}) {
  const { can, timeZone, today, company } = useCompanyScope();
  // O Prontuário abre por cima da Agenda, sem trocar de tela (ADR 0038, adendo de 2026-09-28).
  const [chart, setChart] = useState<{ clientId: string; panel: RecordsPanel } | null>(null);
  // Só estabelecimento tem agenda; unidade de apoio guarda estoque e pessoas (ADR 0039).
  const units = (company.businessUnits ?? []).filter((unit) => unit.kind !== 'SUPPORT');
  const activeBusinessUnitId = company.activeBusinessUnitId ?? null;
  const activeUnit = (company.businessUnits ?? []).find((unit) => unit.id === activeBusinessUnitId);
  return (
    <GatedCompanyScreen allowed={can('scheduling.read')}>
      {/* A agenda é de uma unidade: na visão consolidada, ela pede a unidade antes (ADR 0039). */}
      {activeUnit?.kind === 'SUPPORT' ? (
        <ChooseSchedulingUnit
          subject="agenda"
          units={units}
          reason={`${activeUnit.displayName} é uma unidade de apoio e não tem agenda. Escolha um estabelecimento.`}
        />
      ) : (company.businessUnits ?? []).length > 1 && activeBusinessUnitId === null ? (
        <ChooseSchedulingUnit
          subject="agenda"
          units={units}
          reason="Cada unidade tem a própria agenda. Escolha em qual unidade você vai trabalhar."
        />
      ) : (
        <SchedulingUnitsProvider
          value={{
            units,
            activeBusinessUnitId,
            commissionsEnabled: company.featureKeys.includes('sales.commissions'),
            canRegisterAdvances: can('sales.commissions.close'),
            today,
          }}
        >
          <SchedulingPrepaidProvider
            value={
              can('prepaid_balances.read') && can('prepaid_balances.use')
                ? AgendaPackageChoice
                : null
            }
          >
            <SchedulingBalancesLinkProvider
              value={
                can('clients.read') && can('prepaid_balances.read')
                  ? onOpenClientBalances
                  : undefined
              }
            >
              <SchedulingCreditProvider value={can('sales.read') ? ClientCredit : null}>
                <CompanyScheduling
                  capabilities={{
                    canCreate: can('scheduling.create'),
                    canUpdate: can('scheduling.update'),
                    canCancel: can('scheduling.cancel'),
                    canManageResources: can('scheduling.resources.manage'),
                    canManageBlocks: can('scheduling.blocks.manage'),
                    canDecidePortalRefund: can('sales.payments.manage'),
                  }}
                  timeZone={timeZone}
                  view={view}
                  onView={onView}
                  onOpenClosing={onOpenClosing}
                  onOpenProfessionals={onOpenProfessionals}
                  onOpenChart={
                    can('records.read')
                      ? (clientId) => setChart({ clientId, panel: { kind: 'chart' } })
                      : undefined
                  }
                />
              </SchedulingCreditProvider>
            </SchedulingBalancesLinkProvider>
          </SchedulingPrepaidProvider>
        </SchedulingUnitsProvider>
      )}
      {chart ? (
        <RecordsSidePanel
          panel={chart.panel}
          clientId={chart.clientId}
          timeZone={timeZone}
          today={today}
          canManage={can('records.manage')}
          onClose={() => setChart(null)}
          onPanel={(panel) => setChart({ ...chart, panel })}
          onOpenClientPage={() => onOpenClient(chart.clientId)}
        />
      ) : null}
    </GatedCompanyScreen>
  );
}
